<template>
  <div v-bind="$attrs" class="data-image">
    <el-image :fit="$attrs.fit" :src="url || undefined" @click="openPreview">
      <template #placeholder>
        <img
          v-if="fallbackUrl"
          class="data-image-fallback"
          :src="fallbackUrl"
          :style="{ objectFit: $attrs.fit || 'fill' }"
          alt="图片预览"
        />
        <span v-else class="data-image-status">加载中…</span>
      </template>
      <template #error>
        <img
          v-if="fallbackUrl"
          class="data-image-fallback"
          :src="fallbackUrl"
          :style="{ objectFit: $attrs.fit || 'fill' }"
          alt="图片预览"
        />
        <span
          v-if="failed"
          class="data-image-retry"
          :class="{ 'data-image-retry-overlay': fallbackUrl }"
          role="button"
          tabindex="0"
          @click.stop="load"
          @keydown.enter.stop="load"
        >
          {{ fallbackUrl ? "高清加载失败，重试" : "加载失败，重试" }}
        </span>
        <span v-else-if="!fallbackUrl" class="data-image-status">加载中…</span>
      </template>
    </el-image>
    <el-image-viewer
      v-if="viewerUrl"
      :url-list="[viewerUrl]"
      teleported
      @close="closePreview"
    />
  </div>
</template>

<script>
export default { inheritAttrs: false };
</script>

<script setup>
/* global defineProps */
import { onBeforeUnmount, ref, watch } from "vue";
import { useStore } from "vuex";
import { ElImageViewer } from "element-plus";
import { acquireDataImage, getCachedDataImageUrl } from "@/utils/dataImages";

const props = defineProps({
  imageId: { type: [String, Number], required: true },
  variant: { type: String, default: "original" },
  version: { type: String, default: "" },
  preview: { type: Boolean, default: true },
  thumbnail: { type: Boolean, default: false },
});
const store = useStore();
const url = ref("");
const fallbackUrl = ref("");
const failed = ref(false);
const viewerUrl = ref("");
let fullHandle;
let handle;
let fallbackHandle;
let generation = 0;
function load() {
  const current = ++generation;
  handle?.release();
  fallbackHandle?.release();
  fallbackHandle = null;
  fullHandle?.release();
  fullHandle = null;
  viewerUrl.value = "";
  url.value = "";
  fallbackUrl.value = "";
  failed.value = false;
  const account = store.state.user?.id;
  if (!account || !props.imageId) return;
  // Queue the visible preview before originals can occupy both download slots.
  if (
    !props.thumbnail &&
    !getCachedDataImageUrl(account, props.imageId, props.variant, props.version)
  ) {
    fallbackHandle = acquireDataImage(
      account,
      props.imageId,
      `${props.variant}-thumb`,
      props.version,
      -1
    );
    fallbackUrl.value = fallbackHandle.url;
    fallbackHandle.promise.then(
      (value) => {
        if (current === generation) fallbackUrl.value = value;
      },
      () => {} // The original request can still succeed when its preview fails.
    );
  }
  handle = acquireDataImage(
    account,
    props.imageId,
    props.thumbnail ? `${props.variant}-thumb` : props.variant,
    props.version
  );
  // Read ready cache entries synchronously so reopening never clears the preview.
  url.value = handle.url;
  if (handle.url) fallbackUrl.value = handle.url;
  handle.promise.then(
    (value) => {
      if (current === generation) {
        url.value = value;
        if (viewerUrl.value && !props.thumbnail) viewerUrl.value = value;
      }
    },
    () => {
      if (current === generation) failed.value = true;
    }
  );
}
async function openPreview() {
  if (!props.preview || (!url.value && !fallbackUrl.value)) return;
  if (!props.thumbnail) {
    viewerUrl.value = url.value || fallbackUrl.value;
    return;
  }
  const current = generation;
  if (!fullHandle)
    fullHandle = acquireDataImage(
      store.state.user.id,
      props.imageId,
      props.variant,
      props.version
    );
  viewerUrl.value = fullHandle.url || url.value;
  const requestedHandle = fullHandle;
  try {
    const fullUrl = await requestedHandle.promise;
    if (current === generation && viewerUrl.value) viewerUrl.value = fullUrl;
  } catch {
    requestedHandle.release();
    if (fullHandle === requestedHandle) fullHandle = null;
  }
}
function closePreview() {
  viewerUrl.value = "";
  fullHandle?.release();
  fullHandle = null;
}
watch(
  () => [
    store.state.user?.id,
    props.imageId,
    props.variant,
    props.version,
    props.thumbnail,
  ],
  load,
  { immediate: true }
);
onBeforeUnmount(() => {
  generation++;
  handle?.release();
  fallbackHandle?.release();
  fullHandle?.release();
});
</script>

<style scoped>
.data-image {
  display: inline-block;
}
.data-image > .el-image {
  width: 100%;
  height: 100%;
  display: block;
}
.data-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
}
.data-image-status,
.data-image-retry {
  color: #81918e;
  font-size: 12px;
}
.data-image-retry {
  border: 0;
  background: none;
  cursor: pointer;
  padding: 8px;
}
.data-image-retry-overlay {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  white-space: nowrap;
}
</style>
